import React, { useEffect, useState } from "react";
import { Divider } from "@nextui-org/react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, { Icon } from "leaflet";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import {
  CanceledReason,
  convertEnumToString,
  formatDateTime,
} from "../../lib/utils";
import { FileOutlined, UploadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import BackToButton from "../common/BackToButton";
import { all_routes } from "../router/all_routes";
import StatusBadge from "../common/StatusBadge";
import Header from "../common/header";
import Footer from "../common/footer";
import BookingCheckout from "./BookingCheckout";
import { IMAGE_SERVER_PATH, PUBLIC_API_URL } from "../../environment";
import ClientForm from "../clients/ClientForm";
import ClientsTable from "../clients/ClientsTable";
import { Button, Form, InputNumber, Modal, Upload } from "antd";
import { BookingStatus } from "../../types/booking";
import { ClipboardCopy } from "lucide-react";
import { error } from "console";
import ClientStore from "../../app/stores/clientStore";
import AdminRefundRequestModal from "./AdminRefundRequestModal";
import toast from "react-hot-toast";

const BookingDetails = () => {
  const { id } = useParams();
  const { bookingStore, userStore } = useStore();
  const {
    getBooking,
    currentBooking,
    loadingInitial,
    setStatusCompleted,
    setStatusCanceled,
    setStatusInProcess,
    setStatusPending,
    addPayment,
  } = bookingStore;

  const [canceledReason, setCanceledReason] = useState<string>("");
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [addClientFlag, setaddClientFlag] = useState<boolean>(false);
  const [invoiceDownloaded, setInvoiceDownloaded] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
  const [showAdminRefundModal, setShowAdminRefundModal] = useState(false);
  const [bankCharge, setBankCharge] = useState(0);
  const [bankChargeManuallyEdited, setBankChargeManuallyEdited] =
    useState(false);
const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!bankChargeManuallyEdited) {
      if (selectedPaymentType === "Direct") {
        setBankCharge(0.01); // 1%
      } else if (selectedPaymentType === "Online") {
        setBankCharge(0.03); // 3%
      } else {
        setBankCharge(0);
      }
    }
  }, [selectedPaymentType, bankChargeManuallyEdited]);

  useEffect(() => {
    if (showPaymentModal) {
      setBankChargeManuallyEdited(false);
    }
  }, [showPaymentModal]);

  useEffect(() => {
    if (showPaymentModal && currentBooking?.paymentType) {
      setSelectedPaymentType(currentBooking.paymentType);
    }
  }, [showPaymentModal, currentBooking?.paymentType]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleCancelWithReason = () => {
    const reason: CanceledReason = { reason: canceledReason };
    if (canceledReason.trim()) {
      setStatusCanceled(id ?? "", reason);
    } else {
      alert("Please provide a reason for the cancellation.");
    }
  };

  const [refundAmount, setRefundAmount] = useState<string>("");
  const [price, setAdditionalPrice] = useState<number>(0);

  const [loading, setLoading] = useState(false);

  const onSubmitAdditionalPrice = async () => {
    try {
      if (!currentBooking?.id) {
        toast.error("Booking ID is missing");
        return;
      }

      setLoading(true);
      await bookingStore.updateAdditionalPrice(currentBooking.id, price);
      toast.success("Additional price updated successfully!");
      await bookingStore.getBooking(currentBooking.id); // Refresh data
    } catch (error: any) {
      console.error("Additional price submission error:", error);
      toast.error(error.message || "Failed to update additional price");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   if (id) {
  //     getBooking(id);
  //   }
  //   if (flag) setFlag(false);
  //   if (addClientFlag) setaddClientFlag(false);
  // }, [id, getBooking, flag, addClientFlag]);

useEffect(() => {
  if (id) getBooking(id);
}, [id, getBooking]);

useEffect(() => {
  if (flag) setFlag(false);
}, [flag]);

useEffect(() => {
  if (addClientFlag) setaddClientFlag(false);
}, [addClientFlag]);




  if (loadingInitial || !currentBooking) {
    return <p className="text-gray-600">Loading booking details...</p>;
  }

  const {
    clients,
    customerName,
    phone,
    email,
    address,
    latitude,
    longitude,
    totalPrice,
    serviceName,
    carName,
    bookingCode,
    serviceOptionName,
    serviceOptionFee,
    bookingStatus,
    bookingDate,
    createDate,
    createdBy,
    paymentType,
    updateDate,
    updatedBy,
    fileEntities,
    adultsNumber,
    childrenNumber,
    duration,
    processTime,
    nationalityName,
    reason,
    entryType,
    isGccResident,
    securityDeposit,
    paymentStatus,
    additionalPrice,
    customSecurityDeposit,
    includeSecurityDeposit,
  } = currentBooking;

  const actualTotal = totalPrice ?? 0;
  const totalPaid =
    (totalPrice ?? 0) + parseFloat(currentBooking.refundAmount ?? "0");

  const downloadInvoice = async (id: string) => {
    if (!id) {
      alert("Booking ID not provided");
      return;
    }

    try {
      const response = await fetch(
        `${PUBLIC_API_URL}booking/${id}/booking-invoice`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Invoice download error:", error);
      alert("Failed to download invoice.");
    }
  };

  const handleRefundRequestSuccess = () => {
    // getBooking(id || "");
  };

  const handleRefundSubmit = async () => {
    const parsedAmount = parseFloat(refundAmount);

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert("Please enter a valid refund amount.");
      return;
    }

    if (!id || !totalPrice) {
      alert("Booking not found.");
      return;
    }
    alert(id);

    if (!isValidGuid(id)) {
      alert("Invalid booking ID.");
      return;
    }

    if (parsedAmount > totalPrice) {
      alert("Refund amount exceeds total paid.");
      return;
    }

    if (!refundReason.trim()) {
      alert("Please provide a refund reason.");
      return;
    }

    const payload = {
      id: id,
      refundAmount: parsedAmount,
      reason: refundReason.trim(),
    };

    try {
      const response = await fetch(`${PUBLIC_API_URL}booking/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorText = "Refund failed";

        try {
          const contentType = response.headers.get("content-type") || "";

          if (contentType.includes("application/json")) {
            const errorData = await response.json();
            errorText = errorData?.message || errorText;
          } else {
            errorText = await response.text();
          }
        } catch {
          // Ignore parsing errors
        }
        throw new Error(errorText);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `RefundVoucher_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
   // ✅ Download refund receipt
    await downloadRefundReceipt(id);
      await getBooking(id);
      setShowRefundModal(false);
      setRefundAmount("");
      setRefundReason("");
    } catch (err) {
      console.error("Fetch error object:", err);
      alert("Error processing refund: " + (err || JSON.stringify(err)));
    }
  };

  function isValidGuid(guid: string): boolean {
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  }

  const handleConfirmPayment = async () => {
    if (!id || totalPrice == null) {
      alert("Booking information not available.");
      return;
    }

    if (!authCode.trim()) {
      alert("Please enter authorization code.");
      return;
    }

    const paymentPayload = {
      paymentType: selectedPaymentType,
      bookingId: id,
      amount: totalPrice,
      bankCharge: OnlyBankCharge,
      token: "abc123",
      orderIds: "order_xyz",
      payment: {
        transactionAmount: totalPrice,
        transactionID: authCode,
        createDate: new Date().toISOString(),
        currency: "AED",
        status: "Completed",
        transactionStatus: "Success",
        orderName: `${serviceName} Booking`,
        merchantId: "M123",
      },
    };

    try {
      const res = await addPayment(paymentPayload);
      await getBooking(id);

          await setStatusInProcess(id);

      setShowPaymentModal(false);
      setAuthCode("");
    } catch (err) {
      alert("Payment failed. " + String(err));
    }
  };

  const downloadRefundReceipt = async (id: string) => {
    try {
      const response = await fetch(
        `${PUBLIC_API_URL}booking/${id}/refund-voucher`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download refund receipt.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RefundReceipt_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to download refund receipt.");
    }
  };

  const customIcon = new L.Icon({
    iconUrl: "/location-pin.png",
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

  // Constants
  const bankChargeAmount =
    selectedPaymentType === "Direct" || selectedPaymentType === "Online"
      ? parseFloat((bankCharge * totalPaid).toFixed(2))
      : 0;

  const totalWithBankCharge = parseFloat(
    (totalPaid + bankChargeAmount).toFixed(2)
  );
  const OnlyBankCharge = bankChargeAmount;


const applicantCount = (adultsNumber ?? 0) + (childrenNumber ?? 0);
const requiredFilesCount = applicantCount * 2;
const uploadedFilesCount = fileEntities?.length ?? 0;

const hasNoFiles = uploadedFilesCount === 0;
const hasPartialFiles =
  uploadedFilesCount > 0 && uploadedFilesCount < requiredFilesCount;
const hasAllFiles = uploadedFilesCount >= requiredFilesCount;



const handleFileSelect = ({ fileList }: any) => {
  setSelectedFiles(fileList);
};

const handleUploadSubmit = async () => {
  if (!currentBooking?.id) {
    toast.error("Booking ID missing");
    return;
  }

  if (selectedFiles.length === 0) {
    toast.error("Please select files first");
    return;
  }

  setUploading(true);

  const res = await bookingStore.uploadBookingFiles(currentBooking.id, selectedFiles);

  if (res.status === "success") {
    toast.success(res.data);
    setSelectedFiles([]);
    await getBooking(currentBooking.id);
  } else {
    toast.error(String(res.error));
  }

  setUploading(false);
};



  return (
    <>
      <Header />

      {(userStore.isCashier() || userStore.isAirportCashier() || userStore.isAdmin() ) ? (
        <div className="booking-details-page w-full mt-20">
          <div className="custom-container mx-auto py-6 px-4">
            <div className="border bg-custom-light-blue p-6">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                  Booking Details
                </h1>
                {userStore.isAdmin() && (
                  <BackToButton
                    label="Back to Bookings"
                    href={all_routes.bookingDashboard}
                  />
                )}
              </div>

              <div className="p-4 border rounded-lg bg-gray-50 w-full">
                {/* Combined Customer, Booking Info, and Admin Info in three columns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Customer Info */}
                  <div className="space-y-6">
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">
                      Customer Information
                    </h2>
                    <Divider />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      {customerName && (
                        <div>
                          <p className="font-medium">Name:</p>
                          <p>{customerName}</p>
                        </div>
                      )}
                      {phone && (
                        <div>
                          <p className="font-medium">Phone:</p>
                          <p>{phone}</p>
                        </div>
                      )}
                      {email && (
                        <div>
                          <p className="font-medium">Email:</p>
                          <p className="break-words">{email}</p>
                        </div>
                      )}

                      {adultsNumber && (
                        <div>
                          <p className="font-medium">Adults Qty:</p>
                          <p>{adultsNumber}</p>
                        </div>
                      )}

                      {childrenNumber || childrenNumber === 0 ? (
                        <div>
                          <p className="font-medium">Children Qty:</p>
                          <p>{childrenNumber ?? "0"}</p>
                        </div>
                      ) : (
                        <p></p>
                      )}

                      <div>
                        <p className="font-medium">Is Gcc Resident:</p>
                        <p>{isGccResident ? "Yes" : "No"}</p>
                      </div>

                      {duration && (
                        <div>
                          <p className="font-medium">Duration:</p>
                          <p>{duration}</p>
                        </div>
                      )}
                      {processTime && (
                        <div>
                          <p className="font-medium">Process Time:</p>
                          <p>{processTime}</p>
                        </div>
                      )}
                      {entryType && (
                        <div>
                          <p className="font-medium">Entry Type:</p>
                          <p>{entryType}</p>
                        </div>
                      )}
                      {nationalityName && (
                        <div>
                          <p className="font-medium">Nationality Name:</p>
                          <p>{nationalityName}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Booking Info */}
                  <div className="space-y-6">
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">
                      Booking Information
                    </h2>
                    <Divider />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                      {serviceName && (
                        <div>
                          <p className="font-medium text-gray-800">Service:</p>
                          <p>{serviceName}</p>
                        </div>
                      )}
                      {carName && (
                        <div>
                          <p className="font-medium text-gray-800">Car Name:</p>
                          <p>{carName}</p>
                        </div>
                      )}
                      {bookingCode && (
                        <div>
                          <p className="font-medium text-gray-800">
                            Booking Code:
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                navigator.clipboard.writeText(bookingCode)
                              }
                              title="Click to copy booking code"
                              className="text-left cursor-pointer text-sm text-[#0d3941] hover:text-[#49bbd8] focus:outline-none"
                            >
                              {bookingCode}
                            </button>
                            <button
                              onClick={() =>
                                navigator.clipboard.writeText(bookingCode)
                              }
                              title="Copy booking code"
                            >
                              <ClipboardCopy
                                size={16}
                                className="text-[#0d3941] hover:text-[#49bbd8]"
                              />
                            </button>
                          </div>
                        </div>
                      )}

                      {paymentType && (
                        <div>
                          <p className="font-medium">Payment Type:</p>
                          <span
                            className={`inline-block px-3 py-1 text-sm font-medium rounded ${
                              paymentType
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {paymentType || "Still Not Paid"}
                          </span>
                        </div>
                      )}
                      {/* Pay Now button for direct payment */}
                      {((paymentType === "Direct" &&
                        paymentStatus !== "Completed") ||
                        !paymentType) && (
                        <div className="mt-2">
                          <button
                            onClick={() => setShowPaymentModal(true)}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
                          >
                            Pay Now
                          </button>
                        </div>
                      )}

                      {paymentStatus && (
                        <div>
                          <p className="font-medium">Payment Status:</p>
                          <span
                            className={`inline-block px-3 py-1 text-sm font-medium rounded ${
                              paymentStatus === "Completed" ||
                              paymentStatus === "ChargeSucceeded"
                                ? "bg-green-100 text-green-700"
                                : paymentStatus === "Pending"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                            }`}
                          >
                            {paymentStatus}
                          </span>
                        </div>
                      )}
                      {/* {(paymentStatus === "Completed" ||
                        paymentStatus === "ChargeSucceeded") && (
                        <button
                          onClick={() => id && downloadInvoice(id)}
                          className="px-2 py-2 mt-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Download Invoice
                        </button>
                      )} */}

                              <button
                          onClick={() => id && downloadInvoice(id)}
                          className="px-2 py-2 mt-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Download Invoice
                        </button>


                        
                      {parseFloat(refundAmount) > 0 && (
                        <button
                          onClick={() => {
                            if (id) downloadRefundReceipt(id);
                          }}
                          className="px-2 py-2 mt-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Receipt
                        </button>
                      )}

                      
                      {paymentStatus === "Completed" && (
                        <>
                          {(userStore.isAdmin()||userStore.isCashier()||userStore.isAirportCashier()) && (
                            <div className="md:col-span-2">
                              <Button
                                type="primary"
                                onClick={() => setShowAdminRefundModal(true)}
                                icon={<FileOutlined />}
                              >
                                Manage Refund Requests
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                      {bookingDate && (
                        <div>
                          <p className="font-medium">Booking Date:</p>
                          <p>{formatDateTime(bookingDate.toString())}</p>
                        </div>
                      )}
                      {totalPrice && (
                        <div>
                          <p className="font-medium">Total Price:</p>
                          <p>{totalPrice} AED</p>
                        </div>
                      )}

                 
{serviceName === "Tourist Visa" && (
  <div>
    <p className="font-medium">Security Deposit:</p>
    <p>
      {
        includeSecurityDeposit && Number(customSecurityDeposit) > 0
          ? `${Number(customSecurityDeposit)} AED`
          : `${Number(securityDeposit ?? 0)} AED`
      }
    </p>
  </div>
)}

               
                    {serviceOptionName && (
                        <div>
                          <p className="font-medium text-gray-800">
                            Service Option:
                          </p>
                          <p>{serviceOptionName}</p>
                        </div>
                      )}
                      {serviceOptionFee && (
                        <div>
                          <p className="font-medium text-gray-800">
                            Service Option Fee:
                          </p>
                          <p>{serviceOptionFee} AED</p>
                        </div>
                      )}
                      {bookingStatus && (
                        <div className="md:col-span-2 flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-800">Status:</p>
                            {!showReasonInput && (
                              <StatusBadge status={bookingStatus.toString()} />
                            )}
                          </div>

                          {userStore.isAdmin() && (
                            <div className="flex space-x-2">
                              {bookingStatus ===
                                convertEnumToString(
                                  BookingStatus.InProcess,
                                  BookingStatus
                                ) && (
                                <>
                                  <button
                                    onClick={() => setStatusCompleted(id ?? "")}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg shadow hover:bg-green-600"
                                  >
                                    Completed
                                  </button>
                                  <button
                                    onClick={() => setStatusPending(id ?? "")}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600"
                                  >
                                    Pending
                                  </button>
                                </>
                              )}

                              {bookingStatus ===
                                convertEnumToString(
                                  BookingStatus.Canceled,
                                  BookingStatus
                                ) && (
                                <button
                                  onClick={() => setStatusInProcess(id ?? "")}
                                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg shadow hover:bg-yellow-600"
                                >
                                  In-Progress
                                </button>
                              )}

                              {bookingStatus ===
                                convertEnumToString(
                                  BookingStatus.Pending,
                                  BookingStatus
                                ) && (
                                <div className="flex flex-col">
                                  {!showReasonInput && (
                                    <button
                                      onClick={() => setShowReasonInput(true)}
                                      className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg shadow hover:bg-red-600"
                                    >
                                      Canceled
                                    </button>
                                  )}

                                  {showReasonInput && (
                                    <div className="p-2 col-span-1 flex flex-row ">
                                      <textarea
                                        value={canceledReason}
                                        onChange={(e) =>
                                          setCanceledReason(e.target.value)
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-lg"
                                        placeholder="Please provide a reason for cancellation..."
                                      />
                                      <div className="flex flex-col gap-2">
                                        <button
                                          onClick={handleCancelWithReason}
                                          className="ml-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600"
                                        >
                                          Submit
                                        </button>
                                        <button
                                          onClick={() =>
                                            setShowReasonInput(false)
                                          }
                                          className="ml-2 px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded-lg shadow hover:bg-gray-600"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {!showReasonInput && (
                                    <button
                                      onClick={() =>
                                        setStatusInProcess(id ?? "")
                                      }
                                      className="px-4 py-2 mt-2 text-sm font-medium text-white bg-yellow-500 rounded-lg shadow hover:bg-yellow-600"
                                    >
                                      In-Progress
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Admin Update Section */}
                  {(userStore.isAdmin() || userStore.isAirportCashier() || userStore.isCashier()) && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-gray-700 mb-4">
                        Admin Update Section
                      </h2>
                      <Divider />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                        {createDate && (
                          <div>
                            <p className="font-medium">Create Date:</p>
                            <p>{formatDateTime(createDate?.toString())}</p>
                          </div>
                        )}
                        {createdBy && (
                          <div>
                            <p className="font-medium">Created By:</p>
                            <p>{createdBy}</p>
                          </div>
                        )}

                        {updateDate && (
                          <div>
                            <p className="font-medium">Last Updated:</p>
                            <p>{formatDateTime(updateDate)}</p>
                          </div>
                        )}

                        {updatedBy && (
                          <div>
                            <p className="font-medium">Updated By:</p>
                            <p>{updatedBy}</p>
                          </div>
                        )}

                        {reason && (
                          <div>
                            <p className="font-medium">Reason Of Cancelling:</p>
                            <p>{reason}</p>
                          </div>
                        )}


                        {/* Button to open the modal */}
                        <Button
                          onClick={showModal}
                          className="entry"
                          icon={<FileOutlined />}
                        >
                          View Uploads
                        </Button>
                        <Form
                          layout="vertical"
                          onFinish={onSubmitAdditionalPrice} // Changed from onSubmit to onSubmitAdditionalPrice
                        >
                          <Form.Item label="Additional Price (AED)">
                            <InputNumber
                              min={0}
                              value={price}
                              style={{ width: "100%" }}
                              onChange={(value) =>
                                setAdditionalPrice(value ?? 0)
                              }
                            />
                          </Form.Item>

                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading} // Uses the loading state we defined
                            block
                          >
                            Save
                          </Button>
                        </Form>
                        {additionalPrice !== null &&
                          additionalPrice !== undefined && (
                            <div>
                              <p className="font-medium">Additional Price:</p>
                              <p>{additionalPrice}</p>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                {adultsNumber && clients!.length == 0 && (
                  <div className="w-full">
                    <ClientForm
                      key={`${adultsNumber}-${childrenNumber}`}
                      quantity={(adultsNumber ?? 0) + (childrenNumber ?? 0)}
                      id={id}
                      setaddClientFlag={setaddClientFlag}
                    />
                  </div>
                )}

                {clients!.length! > 0 && (
                  <div className="mt-6">
                    <ClientsTable
                      bookingId={id ?? ""}
                      clients={clients}
                      setFlag={setFlag}
                      email={email}
                      customerName={customerName}
                      phone={phone}
                      status={bookingStatus!}
                    />
                  </div>
                )}
              </div>

              {/* Uploaded Files Section */}
                <div className="mt-6">
                <Modal
  title="Uploaded Files"
  open={isModalVisible}
  onCancel={handleCancel}
  destroyOnHidden
  footer={null}
  width={800}
>
  {/* Existing files preview */}
  {fileEntities && fileEntities.length > 0 && (
    <>
      <Divider />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4">
        {fileEntities.map((file) => (
          <div
            key={file.id}
            className="flex flex-col items-center p-2 bg-white border rounded shadow"
          >
            <a
              target="_blank"
              href={`${IMAGE_SERVER_PATH}/${file.filePath}`}
              rel="noopener noreferrer"
            >
              <div className="w-full h-32 bg-gray-200 rounded flex items-center justify-center mb-2">
                {file.fileName?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                  <img
                    src={`${IMAGE_SERVER_PATH}/${file.filePath}`}
                    alt={file.fileName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl">📄</span>
                )}
              </div>
            </a>
            <p className="text-sm truncate">{file.fileName}</p>
          </div>
        ))}
      </div>
    </>
  )}

  {/* Upload section (NO FILES or PARTIAL FILES) */}
  {(hasNoFiles || hasPartialFiles) && (
    <div className="mt-6 p-4 border border-dashed rounded-lg bg-gray-50 text-center">
      <p className="text-gray-700 mb-2">
        {hasNoFiles
          ? "No documents uploaded yet."
          : `Missing ${
              requiredFilesCount - uploadedFilesCount
            } files.`}
      </p>

<Upload
  multiple
  beforeUpload={() => false}
  onChange={handleFileSelect}
  fileList={selectedFiles}
>
  <Button icon={<UploadOutlined />}>Select Documents</Button>
</Upload>

<Button
  type="primary"
  className="mt-3"
  loading={uploading}
  onClick={handleUploadSubmit}
  disabled={selectedFiles.length === 0}
>
  Submit Upload
</Button>



      <p className="text-xs text-gray-500 mt-2">
        Required: {requiredFilesCount} files (Photo + Passport per applicant)
      </p>
    </div>
  )}

  {/* All files uploaded */}
  {hasAllFiles && (
    <div className="mt-6 text-center text-green-600 font-medium">
      ✅ All required documents have been uploaded.
    </div>
  )}
</Modal>

                </div>
        

              {latitude && longitude && (
                <div className="mt-6">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Booking Location
                  </h2>
                  <div className="h-72 rounded-lg overflow-hidden border">
                    <MapContainer
                      center={[latitude, longitude]}
                      zoom={15}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker
                        position={[latitude, longitude]}
                        icon={customIcon}
                      >
                        <Popup>{address || "Booking Location"}</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
          <AdminRefundRequestModal
            booking={currentBooking}
            open={showAdminRefundModal}
            onClose={() => setShowAdminRefundModal(false)}
            onRefundSuccess={handleRefundRequestSuccess}
          />
        </div>
      ) : (
        <BookingCheckout />
      )}

      {/* Refund Modal */}
      <Modal
        width={1000}
        title="Refund Request"
        visible={showRefundModal}
        onCancel={() => {
          setShowRefundModal(false);
          setRefundAmount("");
          setRefundReason("");
        }}
        footer={null}
      >
        <div className="mx-auto p-4 border border-red-200 rounded space-y-4">
          <div>
            <p className="font-semibold">Service:</p>
            <p>{serviceName}</p>
          </div>
          <div className="w-full border border-gray-300 rounded overflow-hidden">
            <div className="grid grid-cols-4 bg-gray-100 text-sm font-semibold text-gray-700">
              <div className="px-4 py-2 border-r">Total Paid</div>
              <div className="px-4 py-2 border-r">Bal Amount</div>
              <div className="px-4 py-2">Security Deposit</div>
              <div className="px-4 py-2">Refunded Amount</div>
            </div>
            <div className="grid grid-cols-4 text-sm">
              <div className="px-4 py-2 border-r">{totalPaid ?? 0} AED</div>
              <div className="px-4 py-2">{totalPrice ?? 0} AED</div>
              <div className="px-4 py-2">{securityDeposit ?? 0} AED</div>
              <div className="px-4 py-2">
                {currentBooking.refundAmount ?? 0} AED
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Refund Amount (AED)
            </label>
            <input
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter refund amount"
            />
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Reason for Refund
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded resize-y min-h-[120px]"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Enter refund reason..."
            />
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                setShowRefundModal(false);
                setRefundAmount("");
                setRefundReason("");
              }}
              className="px-3 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleRefundSubmit}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Submit Refund
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title="Confirm Payment"
        visible={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
      >
        <div className="space-y-4">
          {/* Total Amount Display */}
          <div>
            <label className="block mb-1 font-semibold">Total Amount</label>
            <div className="p-2 border rounded text-lg font-bold text-[#cc3093]">
              {totalPaid} AED
            </div>
          </div>

          {/* Payment Type Selection */}
          <div>
            <label className="block mb-2 font-semibold">
              Select Payment Type
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                type={selectedPaymentType === "Cash" ? "primary" : "default"}
                onClick={() => setSelectedPaymentType("Cash")}
              >
                Cash
              </Button>

              <Button
                type={selectedPaymentType === "Bank" ? "primary" : "default"}
                onClick={() => setSelectedPaymentType("Bank")}
              >
                Bank Transfer
              </Button>

              <Button
                type={selectedPaymentType === "Direct" ? "primary" : "default"}
                onClick={() => {
                  setSelectedPaymentType("Direct");
                  setBankChargeManuallyEdited(false);
                }}
              >
                POS Machine
              </Button>

              <Button
                type={selectedPaymentType === "Online" ? "primary" : "default"}
                onClick={() => {
                  setSelectedPaymentType("Online");
                  setBankChargeManuallyEdited(false);
                }}
              >
                Online Payment
              </Button>
            </div>
          </div>

          {/* Conditionally show Bank Charge input for Online or POS */}
          {(selectedPaymentType === "Online" ||
            selectedPaymentType === "Direct") && (
            <div>
              <label className="block mb-1 font-semibold">
                Bank Charge Rate (e.g., 0.01 = 1%)
              </label>
              <input
                type="number"
                min="0"
                step="0.0001"
                className="w-full p-2 border rounded"
                value={bankCharge}
                onChange={(e) => {
                  setBankCharge(parseFloat(e.target.value) || 0);
                  setBankChargeManuallyEdited(true);
                }}
                placeholder="Enter charge rate like 0.01 for 1%"
              />
              {/* Show total including bank charge */}
              <div className="mt-2 flex justify-between font-semibold text-[#cc3093]">
                <div>Total with Bank Charge: {totalWithBankCharge} AED</div>
                <div>Bank Charge: {OnlyBankCharge} AED</div>
              </div>
            </div>
          )}

          {/* Authorization Code Input */}
          <div>
            <label className="block mb-1">Authorization Code</label>
            <input
              className="w-full p-2 border rounded"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleConfirmPayment}
              disabled={!selectedPaymentType || !authCode}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
      <Footer />
    </>
  );
};

export default observer(BookingDetails);
